import { toRaw } from 'vue'

import { t } from '@/i18n/i18n'
import { useGalleryStore } from '@/pages/gallery/gallery.store'
import { useGuestsStore } from '@/pages/guests/store/guests.store'
import {
  type PageChoice,
  type PageChoiceData,
  type PageContent,
  type PageContentLocation,
  type PageContentWithElements,
  type PageElement,
  type PageMainImageContent,
  type PageSubMainImageContent,
  sortCarouselEventIds,
  sortCarouselGuestIds,
  withoutImageContents,
} from '@/pages/menuEntries/pageSetup/pageContent.type'
import { PageContentService } from '@/pages/menuEntries/pageSetup/pageContentService'
import { useProgEventStore } from '@/pages/progEvent/progEvent.store'
import { useScenesStore } from '@/pages/scenes/scenes.store'
import { PAGE_MODE, type PageMode } from '@/shared/constants'
import { ErrorService } from '@/shared/service/errorService'
import { SnackbarService } from '@/shared/snackbar/snackbar.service'
import { type MusicGroup } from '@/shared/types/musicGroup/musicGroup.type'
import { create } from '@/shared/utils/zustandVue'

// long enough to read the message, which asks to redo the last change
const MODIFIED_ELSEWHERE_MESSAGE_MS = 10000

/** The server refuses a list of blocks that no longer matches the page (added or deleted elsewhere). */
function isModifiedElsewhere(e: unknown): boolean {
  return (
    (e as { response?: { status?: number } } | null)?.response?.status === 409
  )
}

/** A refused list is not an error to report: tell the user to redo the change on the reloaded page. */
async function handleSaveError(
  e: unknown,
  reload: () => Promise<void>,
): Promise<void> {
  if (isModifiedElsewhere(e)) {
    SnackbarService.error(
      t('PAGESETUP.MODIFIED_ELSEWHERE'),
      MODIFIED_ELSEWHERE_MESSAGE_MS,
    )
    await reload()
  } else {
    ErrorService.handleError(e)
  }
}

// blocks keep the server's `_id` (see editContent)
function serverId(content: object): string | undefined {
  return (content as { _id?: string })._id
}

class PageSetupState {
  mode: PageMode = PAGE_MODE.VIEW
  currentPage: PageContentLocation = 'HOME_PAGE'
  pageContents: Omit<PageContent, 'page'>[] = []
  pageChoices: PageChoice[] = []
  eventChoices: PageChoiceData[] = []
  currentElement:
    | {
        parentContent: PageContentWithElements
        element: PageElement | undefined
        wantedIndex: number | undefined
      }
    | undefined = undefined
  selectedContent: Omit<PageContent, 'page'> | undefined = undefined
  showEditElementModal = false
  showDeleteElementModal = false
  showVisibilityElementModal = false
  showEditContentModal = false
  showDeleteContentModal = false
  showVisibilityContentModal = false
  loading = 0
  mainImageTemp: string | null = null
  subImageTemp: string | null = null
  ratioTemp: number | null = null
  subImageRatioTemp: number | null = null
}

type ShowEditElementModalPayload = {
  element?: PageElement
  content: PageContentWithElements
}
type ShowAddElementModalPayload = {
  content: PageContentWithElements
  index: number
}
type EditElementPayload = {
  content: PageContentWithElements
  element: PageElement
}
type DeleteElementPayload = {
  element: PageElement
  content: PageContentWithElements
}
type AddElementPayload = {
  content: PageContentWithElements
  element: PageElement
  index: number
}
type MoveElementPayload = {
  content: PageContentWithElements
  oldPosition: number
  newPosition: number
  element: PageElement
}

type PageSetupStore = PageSetupState & {
  load: () => void
  unload: () => void
  showEditElementModalAction: (payload: ShowEditElementModalPayload) => void
  showAddElementModal: (payload: ShowAddElementModalPayload) => void
  showDeleteElementModalAction: (payload: ShowEditElementModalPayload) => void
  showVisibilityElementModalAction: (
    payload: ShowEditElementModalPayload,
  ) => void
  showDeleteContentModalAction: (content: PageContent) => void
  showAddContentModal: (content: Omit<PageContent, 'page'>) => void
  showEditContentModalAction: (content: PageContent) => void
  showVisibilityContentModalAction: (content: PageContent) => void
  goToView: () => void
  addImageToContent: () => void
  addSubImageToContent: () => void
  removeImagesFromContent: () => void
  setMainImageTemp: (payload: string | null) => void
  setSubImageTemp: (payload: string | null) => void
  setRatioTemp: (payload: number | null) => void
  setSubImageRatioTemp: (payload: number | null) => void
  setPageChoices: (pageChoices: PageChoice[]) => void
  loadContent: (page: PageContentLocation) => Promise<void>
  saveAllContent: (contents: Omit<PageContent, 'page'>[]) => Promise<boolean>
  addContent: (content: Omit<PageContent, 'page'>) => Promise<void>
  editContent: (content: Omit<PageContent, 'page'>) => Promise<void>
  deleteContent: (content: Omit<PageContent, 'page'>) => Promise<void>
  editElement: (payload: EditElementPayload) => Promise<void>
  deleteElement: (payload: DeleteElementPayload) => Promise<void>
  addElement: (payload: AddElementPayload) => Promise<void>
  moveElement: (payload: MoveElementPayload) => Promise<void>
}

/**
 * Read the guests store's carousel guests. Guests migrated to zustand-vue,
 * so read directly from the store (no longer in the legacy registry).
 */
function getGuestsCarousel(): Record<string, MusicGroup> {
  return useGuestsStore.getState().guests
}

export const usePageSetupStore = create<PageSetupStore>((set, get) => ({
  ...new PageSetupState(),

  load: () => {
    set({ loading: get().loading + 1 })
  },
  unload: () => {
    set({ loading: get().loading - 1 })
  },

  showEditElementModalAction: ({ element, content }) => {
    set({
      mode: PAGE_MODE.EDIT,
      currentElement: {
        parentContent: content,
        element,
        wantedIndex: undefined,
      },
      showEditElementModal: true,
    })
  },
  showAddElementModal: ({ content, index }) => {
    set({
      mode: PAGE_MODE.ADD,
      currentElement: {
        parentContent: content,
        element: undefined,
        wantedIndex: index,
      },
      showEditElementModal: true,
    })
  },
  showDeleteElementModalAction: ({ element, content }) => {
    set({
      mode: PAGE_MODE.DELETE,
      currentElement: {
        parentContent: content,
        element,
        wantedIndex: undefined,
      },
      showDeleteElementModal: true,
    })
  },
  showVisibilityElementModalAction: ({ element, content }) => {
    set({
      mode: PAGE_MODE.TOGGLING_VISIBILITY,
      currentElement: {
        parentContent: content,
        element,
        wantedIndex: undefined,
      },
      showVisibilityElementModal: true,
    })
  },
  showDeleteContentModalAction: (content) => {
    set({
      mode: PAGE_MODE.DELETE,
      selectedContent: content,
      showDeleteContentModal: true,
    })
  },
  showAddContentModal: (content) => {
    set({
      mode: PAGE_MODE.ADD,
      selectedContent: content,
      showEditContentModal: true,
    })
  },
  showEditContentModalAction: (content) => {
    set({
      mode: PAGE_MODE.EDIT,
      selectedContent: content,
      showEditContentModal: true,
    })
  },
  showVisibilityContentModalAction: (content) => {
    set({
      mode: PAGE_MODE.TOGGLING_VISIBILITY,
      selectedContent: content,
      showVisibilityContentModal: true,
    })
  },
  goToView: () => {
    set({
      mode: PAGE_MODE.VIEW,
      loading: get().loading - 1,
      currentElement: undefined,
      showEditElementModal: false,
      showDeleteElementModal: false,
      showEditContentModal: false,
      showDeleteContentModal: false,
      showVisibilityElementModal: false,
      showVisibilityContentModal: false,
    })
  },

  addImageToContent: () => {
    const state = get()
    if (state.pageContents.find((page) => page.type === 'HOME_IMAGE')) {
      return
    }
    const content: PageMainImageContent = {
      image: undefined,
      type: 'HOME_IMAGE',
      videoFilename: undefined,
      page: state.currentPage,
      id: undefined,
      isVisible: true,
    }
    set({ pageContents: [...state.pageContents, content] })
  },
  addSubImageToContent: () => {
    const state = get()
    if (state.pageContents.find((page) => page.type === 'HOME_SUB_IMAGE')) {
      return
    }
    const content: PageSubMainImageContent = {
      image: undefined,
      type: 'HOME_SUB_IMAGE',
      id: undefined,
      page: state.currentPage,
      isVisible: true,
    }
    set({ pageContents: [...state.pageContents, content] })
  },
  removeImagesFromContent: () => {
    set({
      pageContents: withoutImageContents(get().pageContents),
    })
  },
  setMainImageTemp: (payload) => {
    set({ mainImageTemp: payload })
  },
  setSubImageTemp: (payload) => {
    set({ subImageTemp: payload })
  },
  setRatioTemp: (payload) => {
    set({ ratioTemp: payload })
  },
  setSubImageRatioTemp: (payload) => {
    set({ subImageRatioTemp: payload })
  },
  setPageChoices: (pageChoices) => {
    set({ pageChoices })
  },

  async loadContent(page) {
    get().load()
    try {
      set({ currentPage: page, pageContents: [] })
      const galleryRecord = useGalleryStore.getState().gallery
      const [pageChoices, pageContents, eventChoices] = await Promise.all([
        PageContentService.getChoices(),
        PageContentService.getAll(page, galleryRecord),
        PageContentService.getAllEvents(),
      ])
      const guestsCarousel = getGuestsCarousel()
      const mappedPageContents = pageContents.map((content) => {
        if (content.type === 'HOME_COVER_FLOW_GUEST') {
          if (content.mode !== 'program') {
            return {
              ...content,
              guestIds: sortCarouselGuestIds(
                content.guestIds ?? [],
                guestsCarousel,
              ),
            }
          }
          return {
            ...content,
            eventIds: sortCarouselEventIds(
              content.eventIds ?? [],
              useProgEventStore.getState().progEventList,
              useScenesStore.getState().scenes,
              guestsCarousel,
              content.programSort ?? 'by_hour',
            ),
          }
        }
        return content
      })
      set({
        pageChoices,
        eventChoices,
        pageContents: mappedPageContents,
        ratioTemp:
          pageContents.find((content) => content.type === 'HOME_IMAGE')
            ?.ratio ?? null,
        subImageRatioTemp:
          pageContents.find((content) => content.type === 'HOME_SUB_IMAGE')
            ?.ratio ?? null,
      })
    } catch (e) {
      ErrorService.handleError(e)
    } finally {
      get().unload()
    }
  },

  async saveAllContent(contents) {
    get().load()
    try {
      set({ pageContents: contents })
      const contentList = await PageContentService.saveAll(
        contents,
        get().currentPage,
        useGalleryStore.getState().gallery,
      )
      const guestsCarousel = getGuestsCarousel()
      set({
        pageContents: contentList.map((content) => {
          if (content.type === 'HOME_COVER_FLOW_GUEST') {
            if (content.mode !== 'program') {
              return {
                ...content,
                guestIds: sortCarouselGuestIds(
                  content.guestIds ?? [],
                  guestsCarousel,
                ),
              }
            }
            return {
              ...content,
              eventIds: sortCarouselEventIds(
                content.eventIds ?? [],
                useProgEventStore.getState().progEventList,
                useScenesStore.getState().scenes,
                guestsCarousel,
                content.programSort ?? 'by_hour',
              ),
            }
          }
          return content
        }),
      })
      return true
    } catch (e) {
      await handleSaveError(e, async () => get().loadContent(get().currentPage))
      return false
    } finally {
      get().unload()
    }
  },

  async addContent(content) {
    get().load()
    try {
      const contentList = await PageContentService.saveAll(
        [...get().pageContents, content],
        get().currentPage,
        useGalleryStore.getState().gallery,
      )
      // Used to retrieve the id and page of the newly created HomeContent
      set({ pageContents: contentList })
    } catch (e) {
      await handleSaveError(e, async () => get().loadContent(get().currentPage))
    } finally {
      get().unload()
    }
  },

  async editContent(content) {
    get().load()
    try {
      const state = get()
      // selectedContent may be a reactive proxy from the zustandVue bridge
      // (set from a component reading pageSetupStore.selectedContent). Unwrap
      // it with toRaw so the reference comparison against the raw pageContents
      // entries succeeds.
      const rawSelected = toRaw(state.selectedContent) as
        | PageContent
        | undefined
      const foundContent = state.pageContents.find(
        (homeContent) => toRaw(homeContent) === rawSelected,
      )
      if (!foundContent) {
        SnackbarService.error('HomeContent not found inside the list')
        return
      }
      const index = state.pageContents.indexOf(foundContent)
      const updated = { ...foundContent, ...content, page: state.currentPage }
      await PageContentService.saveOne(updated as PageContent)
      if (index !== -1) {
        const next = [...state.pageContents]
        next[index] = updated as PageContent
        set({ pageContents: next })
      }
      set({ selectedContent: updated as PageContent })
    } catch (e) {
      ErrorService.handleError(e)
    } finally {
      get().unload()
    }
  },

  async deleteContent(content) {
    get().load()
    try {
      const state = get()
      // content may be a reactive proxy from the zustandVue bridge
      // (PageContentDeleteModal reads it from the store proxy): unwrap it so
      // the reference comparison against the raw pageContents entries works.
      const rawContent = toRaw(content)
      // only this block: saving the whole list would also remove the blocks added elsewhere since
      const id = serverId(rawContent)
      if (id != null && id !== '') {
        await PageContentService.deleteOne(id)
      }
      const index = state.pageContents.findIndex(
        (pageContent) => toRaw(pageContent) === rawContent,
      )
      if (index > -1) {
        const next = [...state.pageContents]
        next.splice(index, 1)
        set({ pageContents: next })
      }
    } catch (e) {
      ErrorService.handleError(e)
    } finally {
      get().unload()
    }
  },

  async editElement({ content, element }) {
    get().load()
    try {
      const state = get()
      // Find by reference via currentElement instead of .id lookup
      // (same _id issue as editContent above).
      const originalElement = state.currentElement?.element
      const foundElement = originalElement
        ? content.elements.find(
            (listElement) => listElement === originalElement,
          )
        : undefined
      if (!foundElement) {
        console.error('Page element not found')
      }
      Object.assign(foundElement ?? {}, element)
      await PageContentService.saveOne(content)
    } catch (e) {
      ErrorService.handleError(e)
    } finally {
      get().unload()
    }
  },

  async deleteElement({ element, content }) {
    get().load()
    try {
      const rawElement = toRaw(element)
      const index = content.elements.findIndex(
        (listElement) => toRaw(listElement) === rawElement,
      )
      if (index > -1) {
        content.elements.splice(index, 1)
      }
      // PAGE_LIST is an ordered list - keep positions contiguous
      // CUSTOM_* slots are fixed - keep original positions
      if (content.type === 'PAGE_LIST') {
        content.elements.forEach((el, idx) => {
          el.position = idx
        })
      }
      await PageContentService.saveOne(content)
    } catch (e) {
      ErrorService.handleError(e)
    } finally {
      get().unload()
    }
  },

  async addElement({ content, element, index }) {
    get().load()
    try {
      let maxLengthElements: number | undefined = undefined
      switch (content.type) {
        case 'HOME_CUSTOM_THIRD':
          maxLengthElements = 3
          break
        case 'HOME_CUSTOM_HALF':
          maxLengthElements = 2
          break
        case 'HOME_CUSTOM_FULL':
          maxLengthElements = 1
          break
      }
      if (
        maxLengthElements != null &&
        (content.elements.length > maxLengthElements ||
          index >= maxLengthElements)
      ) {
        // Only a debug error, should not happen if the code is valid
        SnackbarService.error(
          'Cannot add another element inside the menu, the array is already at max size or the set index is out of bounds',
        )
      } else {
        // PAGE_LIST is an ordered list- new position as max position+1 to avoid collisions
        // CUSTOM_* slots are fixed - keep original positions
        element.position =
          content.type === 'PAGE_LIST'
            ? Math.max(...content.elements.map((e) => e.position)) + 1
            : index
        // Fill an empty slot in place, otherwise insert (like Vue 2)
        if ((content.elements[index] as PageElement | undefined) == null) {
          content.elements.splice(index, 1, element)
        } else {
          content.elements.splice(index, 0, element)
        }
      }
      await PageContentService.saveOne(content)
      await get().loadContent(get().currentPage)
    } catch (e) {
      ErrorService.handleError(e)
    } finally {
      get().unload()
    }
  },

  async moveElement({ content, oldPosition, newPosition, element }) {
    get().load()
    try {
      if (oldPosition === newPosition) {
        return
      }
      const elements = content.elements.map((el) => ({ ...el }))
      if (newPosition > oldPosition) {
        elements.forEach((el) => {
          if (el.position <= newPosition && el.position >= oldPosition) {
            el.position--
          }
        })
      } else {
        elements.forEach((el) => {
          if (el.position >= newPosition && el.position <= oldPosition) {
            el.position++
          }
        })
      }
      const movedEl = elements.find((el) => el.id === element.id)
      if (movedEl) {
        movedEl.position = newPosition
      }
      elements.sort((e1, e2) => e1.position - e2.position)
      const pageContents = get().pageContents.map((pc) =>
        toRaw(pc) === toRaw(content) ? { ...toRaw(content), elements } : pc,
      )
      set({ pageContents })
      await PageContentService.saveOne({ ...content, elements })
    } catch (e) {
      ErrorService.handleError(e)
    } finally {
      get().unload()
    }
  },
}))
